import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
  GripVertical, 
  Calendar, 
  Navigation, 
  Car, 
  PartyPopper, 
  Download,
  Ticket,
  Sparkles,
  RotateCcw,
  Eye,
  EyeOff,
  ClipboardList,
  Settings,
  SlidersHorizontal,
  Play,
  Home,
  ShoppingBag,
  Megaphone,
  Skull
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';

// Default card configuration
const DEFAULT_CARDS = {
  actionCards: [
    { id: 'admin-panel', label: 'Admin Panel', icon: Settings, visible: true, adminOnly: true },
    { id: 'trip-catalogue', label: 'Trip Catalogue', icon: ClipboardList, visible: true, adminOnly: true },
    { id: 'create-event', label: 'Create an Event', icon: PartyPopper, visible: true },
    { id: 'find-ride', label: 'Find an Event', icon: Calendar, visible: true },
    { id: 'offer-ride', label: 'Offer a Ride', icon: Car, visible: true },
    { id: 'request-ride', label: 'Request a Ride', icon: Navigation, visible: true },
    { id: 'my-tickets', label: 'Ticket Wallet', icon: Ticket, visible: true },
    { id: 'meet-community', label: 'Meet the Community', icon: Play, visible: true },
    { id: 'safe-space', label: 'Safe Space', icon: Home, visible: true },
    { id: 'sk8-shop', label: 'Community Listings', icon: ShoppingBag, visible: true },
    { id: 'ranting-spot', label: 'Unfiltered', icon: Megaphone, visible: true },
    { id: 'floor-tax', label: 'Floor Tax', icon: Skull, visible: true },
    { id: 'download-app', label: 'Download App', icon: Download, visible: true },
    { id: 'customize-dashboard', label: 'Customize Dashboard', icon: SlidersHorizontal, visible: true, hidden: true },
  ],
  sections: [
    { id: 'upcoming-events', label: 'Upcoming Events', icon: Calendar, visible: true },
    { id: 'active-rides', label: 'Available Rides', icon: Car, visible: true },
    { id: 'discover-corners', label: 'Discover Corners', icon: Play, visible: true },
    { id: 'stats', label: 'Stats Strip', icon: Ticket, visible: true },
    { id: 'open-requests', label: 'Riders Need Lifts', icon: Sparkles, visible: true },
    { id: 'my-bookings', label: 'My Bookings / Trips', icon: Ticket, visible: true },
  ]
};

const STORAGE_KEY_PREFIX = 'rollm8s_dashboard_config_';

// Get storage key for a specific user
const getStorageKey = (userId) => {
  return userId ? `${STORAGE_KEY_PREFIX}${userId}` : STORAGE_KEY_PREFIX + 'default';
};

// Get saved config or defaults
export const getDashboardConfig = (userId) => {
  try {
    const storageKey = getStorageKey(userId);
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        actionCards: DEFAULT_CARDS.actionCards.map(defaultCard => {
          const savedCard = parsed.actionCards?.find(c => c.id === defaultCard.id);
          return savedCard ? { ...defaultCard, visible: savedCard.visible } : defaultCard;
        }).sort((a, b) => {
          const aIndex = parsed.actionCards?.findIndex(c => c.id === a.id) ?? 999;
          const bIndex = parsed.actionCards?.findIndex(c => c.id === b.id) ?? 999;
          return aIndex - bIndex;
        }),
        sections: DEFAULT_CARDS.sections.map(defaultSection => {
          const savedSection = parsed.sections?.find(s => s.id === defaultSection.id);
          return savedSection ? { ...defaultSection, visible: savedSection.visible } : defaultSection;
        }).sort((a, b) => {
          const aIndex = parsed.sections?.findIndex(s => s.id === a.id) ?? 999;
          const bIndex = parsed.sections?.findIndex(s => s.id === b.id) ?? 999;
          return aIndex - bIndex;
        })
      };
    }
  } catch (e) {
    console.error('Error loading dashboard config:', e);
  }
  return DEFAULT_CARDS;
};

// Save config for a specific user
const saveDashboardConfig = (config, userId) => {
  try {
    const storageKey = getStorageKey(userId);
    const toSave = {
      actionCards: config.actionCards.map(c => ({ id: c.id, visible: c.visible })),
      sections: config.sections.map(s => ({ id: s.id, visible: s.visible }))
    };
    localStorage.setItem(storageKey, JSON.stringify(toSave));
  } catch (e) {
    console.error('Error saving dashboard config:', e);
  }
};

// Sortable item component using dnd-kit
const SortableItem = ({ item, onToggleVisibility, type }) => {
  const Icon = item.icon;
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors select-none
        ${isDragging ? 'border-primary bg-primary/10 shadow-lg' : 'border-border/50 bg-card/50'}
        ${!item.visible ? 'opacity-60' : ''}
      `}
      data-testid={`sortable-item-${item.id}`}
    >
      {/* Drag handle - only this part triggers drag */}
      <div 
        {...attributes} 
        {...listeners}
        className="cursor-grab active:cursor-grabbing touch-none p-1"
        data-testid={`drag-handle-${item.id}`}
      >
        <GripVertical className="w-5 h-5 text-muted-foreground" />
      </div>

      {/* Icon */}
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${item.visible ? 'bg-primary/20' : 'bg-muted/20'}`}>
        <Icon className={`w-4 h-4 ${item.visible ? 'text-primary' : 'text-muted-foreground'}`} />
      </div>

      {/* Label */}
      <span className={`flex-1 text-sm font-medium ${!item.visible ? 'text-muted-foreground line-through' : ''}`}>
        {item.label}
      </span>

      {/* Visibility toggle */}
      <button
        onClick={() => onToggleVisibility(type, item.id)}
        className={`p-2 rounded-full transition-colors ${item.visible ? 'text-green-500 bg-green-500/10 hover:bg-green-500/20' : 'text-muted-foreground bg-muted/10 hover:bg-muted/20'}`}
        data-testid={`visibility-toggle-${item.id}`}
      >
        {item.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>
    </div>
  );
};

// Sortable list wrapper
const SortableList = ({ items, type, onReorder, onToggleVisibility }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex(item => item.id === active.id);
      const newIndex = items.findIndex(item => item.id === over.id);
      onReorder(type, arrayMove(items, oldIndex, newIndex));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
    >
      <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {items.map((item) => (
            <SortableItem 
              key={item.id} 
              item={item} 
              type={type}
              onToggleVisibility={onToggleVisibility}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

const DashboardCustomizer = ({ open, onOpenChange, onConfigChange, userId, isAdmin }) => {
  const [config, setConfig] = useState(getDashboardConfig(userId));

  useEffect(() => {
    if (open) {
      setConfig(getDashboardConfig(userId));
    }
  }, [open, userId]);

  // Filter out admin-only cards for non-admin users, and hidden cards (like customize-dashboard)
  const filteredActionCards = (isAdmin 
    ? config.actionCards 
    : config.actionCards.filter(card => !card.adminOnly && card.id !== 'admin-panel' && card.id !== 'trip-catalogue')
  ).filter(card => !card.hidden);

  const toggleCardVisibility = (type, id) => {
    setConfig(prev => ({
      ...prev,
      [type]: prev[type].map(item => 
        item.id === id ? { ...item, visible: !item.visible } : item
      )
    }));
  };

  const handleReorder = (type, newItems) => {
    // For non-admins, preserve admin cards in original positions
    if (!isAdmin && type === 'actionCards') {
      const adminCards = config.actionCards.filter(c => c.adminOnly || c.id === 'admin-panel' || c.id === 'trip-catalogue');
      setConfig(prev => ({
        ...prev,
        [type]: [...adminCards, ...newItems]
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        [type]: newItems
      }));
    }
  };

  const handleSave = () => {
    saveDashboardConfig(config, userId);
    onConfigChange?.(config);
    onOpenChange(false);
  };

  const handleReset = () => {
    setConfig(DEFAULT_CARDS);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="dashboard-customizer-modal">
        <DialogHeader>
          <DialogTitle className="font-semibold text-xl">Customize Dashboard</DialogTitle>
          <DialogDescription>
            Drag items to reorder. Tap the eye to show/hide.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Action Cards */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">Action Cards</h3>
            <SortableList
              items={filteredActionCards}
              type="actionCards"
              onReorder={handleReorder}
              onToggleVisibility={toggleCardVisibility}
            />
          </div>

          {/* Dashboard Sections */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">Dashboard Sections</h3>
            <SortableList
              items={config.sections}
              type="sections"
              onReorder={handleReorder}
              onToggleVisibility={toggleCardVisibility}
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border/50">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="text-muted-foreground"
            data-testid="reset-dashboard-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-customizer-btn">
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-primary hover:bg-primary/90" data-testid="save-customizer-btn">
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DashboardCustomizer;
